import { useFormContext } from 'react-hook-form';
import { LwFormMultiSelect } from 'redesign';
import { CompanyListItem } from '@types';
import { useMemo } from 'react';
import { Job } from 'partner/shared/services/partner-checkouts-service.types';
import { InboxFormValues, MultiSelectOption } from './inbox-filter-form.types';

export const BusinessUnitFiltering = ({
  companies,
  jobs,
}: {
  companies: CompanyListItem[];
  jobs: Job[];
}) => {
  const { setValue, watch, control } = useFormContext<InboxFormValues>();
  const selectedCompanies = watch('companies');
  const selectedBranchOffices = watch('branchOffices');

  const companyOptions = useMemo(() => {
    return companies.map((c) => ({ id: c.id, name: c.name })) ?? [];
  }, [companies]);

  const branchOfficeOptions = useMemo(() => {
    if (!selectedCompanies) {
      return [];
    }
    const options = companies
      .flatMap((company) => {
        if (!selectedCompanies.find((c) => c.id === company.id)) {
          return;
        }
        return company.branchOffices.map((bo) => ({
          id: bo.id,
          name: bo.name,
        }));
      })
      .filter((item) => !!item);
    return options as MultiSelectOption[];
  }, [selectedCompanies]);

  const departmentOptions = useMemo(() => {
    if (!selectedBranchOffices || !selectedCompanies) {
      return [];
    }

    const options = companies
      .flatMap((company) => company.branchOffices)
      .flatMap((branchOffice) => {
        if (!selectedBranchOffices.find((bo) => bo.id === branchOffice.id)) {
          return;
        }
        return branchOffice.departments.map((department) => ({
          id: department.id,
          name: department.name,
        }));
      })
      .filter((item) => !!item);

    return options as MultiSelectOption[];
  }, [selectedBranchOffices]);
  const jobOptions = useMemo(() => {
    if (!selectedCompanies) {
      return [];
    }

    const companyById: Record<string, { id: string; name: string }> = {};
    selectedCompanies.forEach((company) => {
      companyById[company.id] = company;
    });

    const jobOptionsRelatedToSelectedCompanies = jobs.filter((job) => !!companyById[job.companyId]);
    if (!jobOptionsRelatedToSelectedCompanies) {
      return [];
    }

    const uniqueJobs: Record<string, { id: string; name: string }> = {};
    jobOptionsRelatedToSelectedCompanies.forEach((job) => {
      uniqueJobs[job.id] = job;
    });

    return Object.values(uniqueJobs);
  }, [selectedCompanies, jobs]);

  return (
    <>
      <LwFormMultiSelect
        name="companies"
        label="Bedrijven"
        control={control}
        onChange={(_e, value) => {
          setValue('companies', value);
          if (value.length == 0) {
            setValue('branchOffices', []);
            setValue('departments', []);
            setValue('jobs', []);
          }
        }}
        options={companyOptions}
        getOptionKey={(option) => option.id}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => {
          return option?.id === value.id;
        }}
        renderOption={(props, option: any) => {
          return (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          );
        }}
      />
      <LwFormMultiSelect
        name="branchOffices"
        label="Vestigingen"
        control={control}
        onChange={(_e, value) => {
          setValue('branchOffices', value);
          if (value.length == 0) {
            setValue('departments', []);
          }
        }}
        options={branchOfficeOptions}
        getOptionKey={(option) => option.id}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => {
          return option?.id === value.id;
        }}
        renderOption={(props, option: any) => {
          return (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          );
        }}
        disabled={!selectedCompanies || selectedCompanies?.length < 1}
      />
      <LwFormMultiSelect
        name="departments"
        label="Afdelingen"
        control={control}
        onChange={(_e, value) => {
          setValue('departments', value);
        }}
        options={departmentOptions}
        getOptionKey={(option) => option.id}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => {
          return option?.id === value.id;
        }}
        renderOption={(props, option: any) => {
          return (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          );
        }}
        disabled={!selectedBranchOffices || selectedBranchOffices?.length < 1}
      />
      <LwFormMultiSelect
        name="jobs"
        label="Functies"
        control={control}
        onChange={(_e, value) => {
          setValue('jobs', value);
        }}
        options={jobOptions}
        getOptionKey={(option) => option.id}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => {
          return option?.id === value.id;
        }}
        renderOption={(props, option: any) => {
          return (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          );
        }}
        disabled={!selectedCompanies || selectedCompanies?.length < 1}
      />
    </>
  );
};
